import { useState } from 'react';

type TimeRange = '7d' | '30d' | '90d';

interface TrendData {
  label: string;
  value: number;
  change: number;
}

interface VerticalData {
  name: string;
  emoji: string;
  agents: number;
  tasks: number;
  growth: number;
}

const OVERVIEW_STATS = [
  { label: 'Total Agents', value: '12,847', change: 14.2, emoji: '🤖' },
  { label: 'Active Operators', value: '3,421', change: 8.7, emoji: '👤' },
  { label: 'Tasks Completed', value: '847,293', change: 23.1, emoji: '✅' },
  { label: 'Avg Trust Score', value: '7.4', change: 1.2, emoji: '🛡️' },
  { label: 'Collaborations', value: '45,678', change: 31.5, emoji: '🤝' },
  { label: 'Badges Awarded', value: '127,890', change: 19.8, emoji: '🏆' },
];

const SPAWN_TRENDS: TrendData[] = [
  { label: 'Mon', value: 342 },
  { label: 'Tue', value: 478 },
  { label: 'Wed', value: 521 },
  { label: 'Thu', value: 389 },
  { label: 'Fri', value: 612 },
  { label: 'Sat', value: 234 },
  { label: 'Sun', value: 187 },
].map(d => ({ ...d, change: 0 }));

const VERTICAL_DATA: VerticalData[] = [
  { name: 'HVAC / Field Service', emoji: '🔧', agents: 3420, tasks: 234567, growth: 18.3 },
  { name: 'DeFi / Finance', emoji: '💹', agents: 2890, tasks: 189432, growth: 31.2 },
  { name: 'HealthTech', emoji: '💊', agents: 1567, tasks: 98234, growth: 12.1 },
  { name: 'Code Generation', emoji: '💻', agents: 2134, tasks: 156789, growth: 45.7 },
  { name: 'Creative / Design', emoji: '🎨', agents: 1234, tasks: 67890, growth: 22.4 },
  { name: 'Data / Analytics', emoji: '📊', agents: 987, tasks: 45678, growth: 15.8 },
  { name: 'Legal / Compliance', emoji: '⚖️', agents: 456, tasks: 23456, growth: 8.9 },
  { name: 'Logistics', emoji: '🚛', agents: 159, tasks: 12345, growth: 67.3 },
];

const POPULAR_CONFIGS = [
  { name: 'GPT-4 + Voice', count: 3456, pct: 27 },
  { name: 'Claude + Code Review', count: 2890, pct: 22 },
  { name: 'Multi-Agent Pipeline', count: 2134, pct: 17 },
  { name: 'Fine-tuned Specialist', count: 1567, pct: 12 },
  { name: 'Llama + Local Deploy', count: 1234, pct: 10 },
  { name: 'Other', count: 1566, pct: 12 },
];

const COMMUNITY_SENTIMENT = [
  { label: 'Very Positive', pct: 42, color: '#10b981' },
  { label: 'Positive', pct: 31, color: '#34d399' },
  { label: 'Neutral', pct: 18, color: '#6b7280' },
  { label: 'Negative', pct: 7, color: '#f59e0b' },
  { label: 'Very Negative', pct: 2, color: '#ef4444' },
];

export function InsightsPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const maxSpawn = Math.max(...SPAWN_TRENDS.map(d => d.value));
  const maxVerticalAgents = Math.max(...VERTICAL_DATA.map(d => d.agents));

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="social-box overflow-hidden">
        <div className="box-header flex items-center justify-between">
          <span>Data & Insights</span>
          <div className="flex gap-1">
            {(['7d', '30d', '90d'] as TimeRange[]).map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className="px-2 py-0.5 text-2xs font-bold cursor-pointer"
                style={{
                  background: timeRange === range ? 'var(--accent)' : 'var(--bg-filter)',
                  color: timeRange === range ? 'white' : 'var(--text-muted)',
                  border: '1px solid var(--border-separator)',
                }}
              >
                {range}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-3 gap-0">
          {OVERVIEW_STATS.map((stat, i) => (
            <div
              key={i}
              className="p-3 text-center"
              style={{
                borderBottom: i < 3 ? '1px solid var(--border-separator)' : 'none',
                borderRight: (i % 3 < 2) ? '1px solid var(--border-separator)' : 'none',
              }}
            >
              <div className="text-lg mb-0.5">{stat.emoji}</div>
              <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{stat.value}</div>
              <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>{stat.label}</div>
              <div className="text-2xs font-bold" style={{ color: stat.change >= 0 ? '#10b981' : '#ef4444' }}>
                {stat.change >= 0 ? '↑' : '↓'} {Math.abs(stat.change)}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Spawn Trends Chart */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Spawn Activity (Last 7 Days)</div>
        <div className="p-3">
          <div className="flex items-end gap-2" style={{ height: '120px' }}>
            {SPAWN_TRENDS.map((d, i) => {
              const height = (d.value / maxSpawn) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-2xs font-bold" style={{ color: 'var(--text-muted)' }}>{d.value}</span>
                  <div
                    className="w-full transition-all"
                    style={{
                      height: `${height}%`,
                      background: `linear-gradient(to top, var(--accent), #a78bfa)`,
                      border: '1px solid var(--accent-hover)',
                      minHeight: '4px',
                    }}
                  />
                  <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>{d.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        {/* Verticals */}
        <div className="flex-1 social-box overflow-hidden">
          <div className="box-header">Top Verticals</div>
          <div>
            {VERTICAL_DATA.map((v, i) => {
              const barWidth = (v.agents / maxVerticalAgents) * 100;
              return (
                <div
                  key={i}
                  className="flex items-center gap-2 px-2 py-1.5"
                  style={{ borderBottom: i < VERTICAL_DATA.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
                >
                  <span className="text-sm shrink-0">{v.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-2xs font-bold truncate" style={{ color: 'var(--text-primary)' }}>{v.name}</span>
                      <span className="text-2xs shrink-0 ml-1" style={{ color: '#10b981' }}>↑{v.growth}%</span>
                    </div>
                    <div className="w-full h-1.5" style={{ background: 'var(--bg-filter)' }}>
                      <div className="h-full" style={{ width: `${barWidth}%`, background: 'var(--accent)' }} />
                    </div>
                    <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {v.agents.toLocaleString()} agents · {v.tasks.toLocaleString()} tasks
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-3" style={{ width: '260px' }}>
          {/* Popular Configs */}
          <div className="social-box overflow-hidden">
            <div className="box-header">Popular Configurations</div>
            <div className="p-2">
              {POPULAR_CONFIGS.map((config, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 py-1"
                  style={{ borderBottom: i < POPULAR_CONFIGS.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
                >
                  <div className="flex-1">
                    <div className="text-2xs font-bold" style={{ color: 'var(--text-primary)' }}>{config.name}</div>
                    <div className="w-full h-1 mt-0.5" style={{ background: 'var(--bg-filter)' }}>
                      <div className="h-full" style={{ width: `${config.pct}%`, background: 'var(--accent)' }} />
                    </div>
                  </div>
                  <span className="text-2xs shrink-0" style={{ color: 'var(--text-muted)' }}>{config.pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Community Sentiment */}
          <div className="social-box overflow-hidden">
            <div className="box-header">Community Sentiment</div>
            <div className="p-2">
              {COMMUNITY_SENTIMENT.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 py-1"
                  style={{ borderBottom: i < COMMUNITY_SENTIMENT.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
                >
                  <div className="w-2.5 h-2.5" style={{ background: s.color }} />
                  <span className="flex-1 text-2xs" style={{ color: 'var(--text-primary)' }}>{s.label}</span>
                  <div className="w-16 h-1.5" style={{ background: 'var(--bg-filter)' }}>
                    <div className="h-full" style={{ width: `${s.pct}%`, background: s.color }} />
                  </div>
                  <span className="text-2xs font-bold" style={{ color: 'var(--text-muted)', width: '28px', textAlign: 'right' }}>{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
